import type { ProjectDirectMessagesRealtimeEvent } from "@shared/types";
import * as Ably from "ably";
import {
	createProjectDirectMessagesRealtimeAuth,
	type ProjectDirectMessagesRealtimeAuthResponse,
} from "./project-direct-messages-api.service";

export interface ProjectDirectMessagesRealtimeConnection {
	disconnect: () => void;
}

export async function connectProjectDirectMessagesRealtime(options: {
	projectId: string;
	onEvent: (event: ProjectDirectMessagesRealtimeEvent) => void;
	onConnected?: () => void;
	onError?: (error: unknown) => void;
}): Promise<ProjectDirectMessagesRealtimeConnection> {
	const initialAuth = await createProjectDirectMessagesRealtimeAuth(
		options.projectId,
	);
	let nextAuth: ProjectDirectMessagesRealtimeAuthResponse | null = initialAuth;
	const client = new Ably.Realtime({
		authCallback: async (_tokenParams, callback) => {
			try {
				const auth =
					nextAuth ??
					(await createProjectDirectMessagesRealtimeAuth(options.projectId));
				nextAuth = null;
				callback(null, auth.tokenRequest);
			} catch (error) {
				options.onError?.(error);
				callback("Realtime auth failed", null);
			}
		},
	});
	const channel = client.channels.get(initialAuth.channel);
	const connectedListener = () => options.onConnected?.();
	const failedListener = (stateChange: unknown) =>
		options.onError?.(stateChange);
	client.connection.on("connected", connectedListener);
	client.connection.on("failed", failedListener);
	await channel.subscribe("project-dm.message.created", (message) => {
		options.onEvent(message.data as ProjectDirectMessagesRealtimeEvent);
	});
	return {
		disconnect: () => {
			channel.unsubscribe("project-dm.message.created");
			client.connection.off("connected", connectedListener);
			client.connection.off("failed", failedListener);
			client.close();
		},
	};
}
